import React, { useState, useEffect, useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import ThemeToggle from "./ThemeToggle";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import HttpsRoundedIcon from "@mui/icons-material/HttpsRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import NoAccountsRoundedIcon from "@mui/icons-material/NoAccountsRounded";
import { QRCodeSVG } from "qrcode.react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  setDoc,
  getDoc,
  doc,
  updateDoc,
  increment,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { db, auth } from "./firebaseConfig";
import LoadingOverlay from "./overlay";
import Dashboard from "./Dashboard";
import UpgradeModal from "./UpgradeModal";
import BenefitsTable from "./BenefitsTable";
import DeleteAccountDialog from "./DeleteAccountDialog";
import PrivacyPolicy from "./PrivacyPolicy";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
function generateCode() {
  return Array.from({ length: 6 }, () =>
    CHARS.charAt(Math.floor(Math.random() * CHARS.length))
  ).join("");
}

// ── Client-side rate limiter: max 10 links per 60 s per browser ──
const RL_KEY = "blinkz_rl";
function checkRateLimit() {
  const now = Date.now();
  const history = JSON.parse(localStorage.getItem(RL_KEY) || "[]");
  const recent = history.filter((t) => now - t < 60_000);
  if (recent.length >= 10) return false;
  recent.push(now);
  localStorage.setItem(RL_KEY, JSON.stringify(recent));
  return true;
}

const buildTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: { main: "#F77500", contrastText: "#ffffff" },
      background:
        mode === "light"
          ? { default: "#FFFBFE", paper: "#F3EDF7" }
          : { default: "#141218", paper: "#211F26" },
    },
    shape: { borderRadius: 12 },
    typography: { fontFamily: '"Roboto", "Segoe UI", sans-serif' },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 50,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.975rem",
          },
        },
      },
      MuiCard: {
        styleOverrides: { root: { borderRadius: 28, boxShadow: "none" } },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 16 } },
      },
    },
  });

export default function App() {
  const [mode, setMode] = useState(
    () => JSON.parse(localStorage.getItem("darkMode")) || "light"
  );
  const theme = useMemo(() => buildTheme(mode), [mode]);

  // Auth
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  // View: "home" | "dashboard"
  const [view, setView] = useState("home");

  // Shortener form
  const [isCustom, setIsCustom] = useState(false);
  const [customSlug, setCustomSlug] = useState("");
  const [url, setUrl] = useState("");
  const [newLink, setNewLink] = useState("");
  const [converting, setConverting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [showResult, setShowResult] = useState(false);

  // Global stats
  const [stats, setStats] = useState({ linksShortened: 1438, proUsers: 67 });

  // Modals & notifications
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));
  const notify = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  // Auth listener + premium check
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const subsSnap = await getDocs(
            query(
              collection(db, "customers", firebaseUser.uid, "subscriptions"),
              where("status", "in", ["trialing", "active"])
            )
          );
          setIsPremium(!subsSnap.empty);
        } catch {
          setIsPremium(false);
        }
      } else {
        setIsPremium(false);
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  // Global stats listener
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "stats", "global"), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setStats({
          linksShortened: d.linksShortened ?? 1438,
          proUsers: d.proUsers ?? 67,
        });
      }
    });
    return unsub;
  }, []);

  // Handle Stripe success redirect (?upgraded=true)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "true") {
      notify("Payment successful! Your account will be upgraded shortly.", "success");
      window.history.replaceState({}, "", window.location.pathname);
      // Re-check premium after a delay for webhook propagation
      setTimeout(async () => {
        if (auth.currentUser) {
          const snap = await getDocs(
            query(
              collection(db, "customers", auth.currentUser.uid, "subscriptions"),
              where("status", "in", ["trialing", "active"])
            )
          );
          setIsPremium(!snap.empty);
        }
      }, 4000);
    }
  }, []);

  // Redirect detection on initial load
  useEffect(() => {
    const path = window.location.pathname.substring(1);
    if (!path) return;
    // Reserved app paths — don't try to resolve as short links
    if (path === "privacy") return;
    setRedirecting(true);

    const resolve = (snap, ref) => {
      updateDoc(ref, { clicks: increment(1) }).catch(() => {});
      window.location.replace(snap.data()["URL"]);
    };
    const notFound = () => { notify("This link does not exist.", "error"); setRedirecting(false); };
    const failed   = () => { notify("Could not resolve link.", "error");  setRedirecting(false); };

    // Backward compat: old links used /-slug
    if (path.startsWith("-")) {
      const ref = doc(db, "linksCustom", path.substring(1));
      getDoc(ref).then((s) => s.exists() ? resolve(s, ref) : notFound()).catch(failed);
      return;
    }

    // Try random links first, then custom (1 read for random, 2 for custom)
    const rRef = doc(db, "links", path);
    getDoc(rRef).then(async (rSnap) => {
      if (rSnap.exists()) { resolve(rSnap, rRef); return; }
      const cRef = doc(db, "linksCustom", path);
      const cSnap = await getDoc(cRef);
      cSnap.exists() ? resolve(cSnap, cRef) : notFound();
    }).catch(failed);
  }, []);

  const toggleMode = () => {
    const next = mode === "light" ? "dark" : "light";
    setMode(next);
    localStorage.setItem("darkMode", JSON.stringify(next));
  };

  const signIn = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user") notify("Sign-in failed.", "error");
    }
  };

  const signOut = async () => {
    setAnchorEl(null);
    await firebaseSignOut(auth);
    setView("home");
  };

  const handleCustomToggle = (e) => {
    if (!isCustom) {
      if (!user) { signIn(); return; }
      if (!isPremium) { setUpgradeOpen(true); return; }
    }
    setIsCustom(e.target.checked);
  };

  const convertLink = async () => {
    if (!user) { signIn(); return; }
    if (!url.trim()) { notify("Please enter a URL.", "warning"); return; }
    if (!checkRateLimit()) {
      notify("Too many links created. Please wait a moment and try again.", "warning");
      return;
    }
    const normalizedUrl = /^https?:\/\//i.test(url.trim())
      ? url.trim()
      : "https://" + url.trim();
    setConverting(true);
    try {
      if (isCustom && customSlug.trim().length > 1) {
        if (!/^[a-zA-Z0-9]+$/.test(customSlug)) {
          notify("Alias can only contain letters and numbers.", "warning");
          setConverting(false);
          return;
        }
        const snap = await getDoc(doc(db, "linksCustom", customSlug));
        if (snap.exists()) {
          notify("Alias already taken — try another.", "error");
          setConverting(false);
          return;
        }
        await setDoc(doc(db, "linksCustom", customSlug), {
          URL: normalizedUrl,
          uid: user?.uid || null,
          clicks: 0,
          createdAt: serverTimestamp(),
        });
        setDoc(doc(db, "stats", "global"), { linksShortened: increment(1) }, { merge: true }).catch(() => {});
        setNewLink(window.location.hostname + "/" + customSlug);
      } else {
        let newCode;
        let taken = true;
        while (taken) {
          newCode = generateCode();
          const snap = await getDoc(doc(db, "links", newCode));
          taken = snap.exists();
        }
        await setDoc(doc(db, "links", newCode), {
          URL: normalizedUrl,
          uid: user?.uid || null,
          clicks: 0,
          createdAt: serverTimestamp(),
        });
        setDoc(doc(db, "stats", "global"), { linksShortened: increment(1) }, { merge: true }).catch(() => {});
        setNewLink(window.location.hostname + "/" + newCode);
      }
      setShowResult(true);
    } catch (e) {
      console.error(e);
      notify("Something went wrong.", "error");
    }
    setConverting(false);
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text || "https://" + newLink);
    notify("Copied to clipboard!");
  };

  const reset = () => {
    setUrl("");
    setCustomSlug("");
    setNewLink("");
    setShowResult(false);
    setIsCustom(false);
  };

  // Privacy page — early return with minimal chrome
  if (window.location.pathname === "/privacy") {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          component="header"
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: { xs: 2.5, sm: 5 }, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
            onClick={() => window.location.assign("/")}
          >
            <Box component="img" src="/logo.png" alt="Blinkz" sx={{ width: 28, height: 28 }} />
            <Typography variant="h6" fontWeight={800} color="primary" letterSpacing={-0.5} sx={{ userSelect: "none" }}>
              Blinkz
            </Typography>
          </Box>
          <ThemeToggle mode={mode} onToggle={toggleMode} />
        </Box>
        <PrivacyPolicy />
        <Box
          component="footer"
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 3, px: 3, py: 3, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Typography variant="caption" color="text.disabled" sx={{ cursor: "pointer" }} onClick={() => window.location.assign("/")}>
            © {new Date().getFullYear()} BlinkzUS
          </Typography>
          <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ cursor: "pointer" }} onClick={() => window.location.assign("/privacy")}>
            Privacy Policy
          </Typography>
          <Typography component="a" href="mailto:imintcomx@gmail.com" variant="caption" color="text.disabled" sx={{ textDecoration: "none" }}>
            Support
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {redirecting && <LoadingOverlay />}

      {/* Header */}
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2.5, sm: 5 },
          py: 1.5,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
          onClick={() => window.location.assign("/")}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="Blinkz"
            sx={{ width: 28, height: 28, flexShrink: 0 }}
          />
          <Typography
            variant="h6"
            fontWeight={800}
            color="primary"
            letterSpacing={-0.5}
            sx={{ userSelect: "none" }}
          >
            Blinkz
          </Typography>
          {isPremium && (
            <Chip
              label="PRO"
              size="small"
              color="primary"
              icon={<WorkspacePremiumRoundedIcon sx={{ fontSize: "0.7rem !important" }} />}
              sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {user && (
            <Tooltip title="My Links">
              <IconButton
                size="medium"
                onClick={() => setView(view === "dashboard" ? "home" : "dashboard")}
                color={view === "dashboard" ? "primary" : "default"}
              >
                <DashboardRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
          <ThemeToggle mode={mode} onToggle={toggleMode} />
          {authLoading ? (
            <CircularProgress size={20} sx={{ mx: 1 }} />
          ) : user ? (
            <>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ ml: 0.5 }}>
                <Avatar
                  src={user.photoURL}
                  alt={user.displayName}
                  sx={{ width: 32, height: 32, fontSize: "0.875rem" }}
                >
                  {user.displayName?.[0]}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{ sx: { borderRadius: 3, mt: 1, minWidth: 180 } }}
              >
                <MenuItem disabled sx={{ fontSize: "0.8rem", opacity: "1 !important" }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {user.email}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => { setAnchorEl(null); setView("dashboard"); }}>
                  <ListItemIcon><DashboardRoundedIcon fontSize="small" /></ListItemIcon>
                  My Links
                </MenuItem>
                {!isPremium && (
                  <MenuItem onClick={() => { setAnchorEl(null); setUpgradeOpen(true); }}>
                    <ListItemIcon><WorkspacePremiumRoundedIcon fontSize="small" color="primary" /></ListItemIcon>
                    Upgrade to Pro — $13.99/yr
                  </MenuItem>
                )}
                <MenuItem onClick={signOut}>
                  <ListItemIcon><LogoutRoundedIcon fontSize="small" /></ListItemIcon>
                  Sign out
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  onClick={() => { setAnchorEl(null); setDeleteAccountOpen(true); }}
                  sx={{ color: "error.main" }}
                >
                  <ListItemIcon>
                    <NoAccountsRoundedIcon fontSize="small" sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  Delete account
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<LoginRoundedIcon />}
              onClick={signIn}
              sx={{ ml: 0.5, borderRadius: 50 }}
            >
              Sign in
            </Button>
          )}
        </Box>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: 2,
          pt: { xs: 4, sm: 6 },
          pb: 10,
        }}
      >
        {view === "dashboard" && user ? (
          <Dashboard
            user={user}
            isPremium={isPremium}
            onUpgrade={() => setUpgradeOpen(true)}
            onNewLink={() => { reset(); setView("home"); }}
            onCopyNotify={() => notify("Copied to clipboard!")}
          />
        ) : (
          <>
            {/* Hero */}
            <Typography
              component="h1"
              fontWeight={800}
              textAlign="center"
              letterSpacing={-1.5}
              sx={{ fontSize: { xs: "2.25rem", sm: "3.25rem" }, lineHeight: 1.1, mb: 1.5 }}
            >
              Short links,{" "}
              <Box component="span" color="primary.main">
                instantly.
              </Box>
            </Typography>
            <Typography
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 3.5, maxWidth: 380, fontSize: "1rem" }}
            >
              Paste any URL and get a clean, shareable short link in seconds.
            </Typography>

            {/* Stats mini-cards */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 5,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {[
                { icon: <LinkRoundedIcon sx={{ fontSize: 20, color: "primary.main" }} />, value: stats.linksShortened.toLocaleString(), label: "links shortened" },
                { icon: <GroupsRoundedIcon sx={{ fontSize: 20, color: "primary.main" }} />, value: stats.proUsers.toLocaleString(), label: "Pro users" },
              ].map(({ icon, value, label }) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2.5,
                    py: 1.5,
                    bgcolor: "background.paper",
                    borderRadius: 4,
                    minWidth: 160,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      "& svg": { color: "#fff !important" },
                    }}
                  >
                    {icon}
                  </Box>
                  <Box>
                    <Typography fontWeight={800} fontSize="1.3rem" color="primary.main" letterSpacing={-0.5} lineHeight={1.1}>
                      {value}
                    </Typography>
                    <Typography fontSize="0.72rem" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.4}>
                      {label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Form card */}
            <Collapse in={!showResult} unmountOnExit>
              <Card
                sx={{
                  width: { xs: "calc(100vw - 32px)", sm: 500 },
                  maxWidth: 500,
                  bgcolor: "background.paper",
                }}
              >
                <CardContent
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5, p: { xs: 3, sm: 4 } }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isCustom}
                        onChange={handleCustomToggle}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography fontWeight={500} fontSize="0.925rem">
                          {isCustom ? "Custom alias" : "Random alias"}
                        </Typography>
                        {!isPremium && (
                          <Tooltip title="Requires Blinkz Pro">
                            <LockRoundedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />

                  <Collapse in={isCustom} unmountOnExit>
                    <TextField
                      fullWidth
                      label="Custom alias"
                      placeholder="my-brand"
                      value={customSlug}
                      onChange={(e) => setCustomSlug(e.target.value)}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography color="text.disabled" variant="body2" noWrap>
                                {window.location.hostname + "/"}
                              </Typography>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Collapse>

                  <TextField
                    fullWidth
                    label="Destination URL"
                    placeholder="https://example.com/a-very-long-url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && convertLink()}
                  />

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={convertLink}
                    disabled={converting}
                    startIcon={
                      converting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <AddLinkRoundedIcon />
                      )
                    }
                    sx={{ py: 1.5 }}
                  >
                    {converting ? "Shortening…" : "Shorten"}
                  </Button>

                  {!user && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                      fontSize="0.8rem"
                    >
                      <Box
                        component="span"
                        sx={{ cursor: "pointer", color: "primary.main", fontWeight: 600 }}
                        onClick={signIn}
                      >
                        Sign in
                      </Box>{" "}
                      to start shortening links.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Collapse>

            {/* Result card */}
            <Collapse in={showResult} unmountOnExit>
              <Card
                sx={{
                  width: { xs: "calc(100vw - 32px)", sm: 500 },
                  maxWidth: 500,
                  bgcolor: "background.paper",
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    p: { xs: 3, sm: 4 },
                  }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    Your link is ready
                  </Typography>

                  <Box
                    onClick={() => copy()}
                    sx={{
                      px: 3,
                      py: 1.25,
                      borderRadius: 50,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      transition: "opacity 0.15s",
                      "&:hover": { opacity: 0.85 },
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Typography fontWeight={700} fontSize="0.95rem" noWrap>
                      {newLink}
                    </Typography>
                    <ContentCopyRoundedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                  </Box>

                  {isPremium ? (
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        bgcolor: "#ffffff",
                        display: "inline-flex",
                        boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
                      }}
                    >
                      <QRCodeSVG
                        value={"https://" + newLink}
                        size={180}
                        fgColor="#141218"
                        bgColor="#ffffff"
                        level="M"
                      />
                    </Box>
                  ) : (
                    <Box
                      onClick={() => setUpgradeOpen(true)}
                      sx={{ position: "relative", display: "inline-flex", cursor: "pointer" }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 4,
                          bgcolor: "#ffffff",
                          display: "inline-flex",
                          boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
                          filter: "blur(6px)",
                          userSelect: "none",
                          pointerEvents: "none",
                        }}
                      >
                        <QRCodeSVG
                          value={"https://" + newLink}
                          size={180}
                          fgColor="#141218"
                          bgColor="#ffffff"
                          level="M"
                        />
                      </Box>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          borderRadius: 4,
                        }}
                      >
                        <LockRoundedIcon sx={{ fontSize: 28 }} />
                        <Typography fontWeight={700} fontSize="0.8rem">
                          Pro feature
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={reset}
                      sx={{ py: 1.25 }}
                    >
                      New link
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => copy()}
                      startIcon={<ContentCopyRoundedIcon />}
                      sx={{ py: 1.25 }}
                    >
                      Copy
                    </Button>
                  </Box>

                  {user && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<DashboardRoundedIcon />}
                      onClick={() => setView("dashboard")}
                      sx={{ color: "text.secondary", borderRadius: 50 }}
                    >
                      View in My Links
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Collapse>

            {/* Trust & safety badges */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                justifyContent: "center",
                mt: 3,
                mb: 1,
                width: { xs: "calc(100vw - 32px)", sm: 500 },
                maxWidth: 500,
              }}
            >
              {[
                { icon: <HttpsRoundedIcon sx={{ fontSize: 15 }} />, label: "HTTPS Encrypted" },
                { icon: <VerifiedUserRoundedIcon sx={{ fontSize: 15 }} />, label: "Spam Protection" },
                { icon: <BoltRoundedIcon sx={{ fontSize: 15 }} />, label: "Instant Redirects" },
                { icon: <LockRoundedIcon sx={{ fontSize: 15 }} />, label: "Secure by Default" },
              ].map(({ icon, label }) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 50,
                    border: "1px solid",
                    borderColor: "divider",
                    color: "text.secondary",
                  }}
                >
                  {icon}
                  <Typography fontSize="0.72rem" fontWeight={600} letterSpacing={0.2}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Benefits table for non-Pro users */}
            {!isPremium && (
              <BenefitsTable onUpgrade={() => setUpgradeOpen(true)} />
            )}
          </>
        )}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: { xs: 1.5, sm: 3 },
          px: 3,
          py: 3,
          borderTop: "1px solid",
          borderColor: "divider",
          mt: "auto",
        }}
      >
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ cursor: "pointer" }}
          onClick={() => window.location.assign("/")}
        >
          © {new Date().getFullYear()} BlinkzUS
        </Typography>
        <Typography
          variant="caption"
          color="primary.main"
          fontWeight={600}
          sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          onClick={() => window.location.assign("/privacy")}
        >
          Privacy Policy
        </Typography>
        <Typography
          component="a"
          href="mailto:imintcomx@gmail.com"
          variant="caption"
          color="text.disabled"
          sx={{ textDecoration: "none", "&:hover": { color: "text.secondary" } }}
        >
          Support
        </Typography>
      </Box>

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        user={user}
        onSignIn={signIn}
      />

      <DeleteAccountDialog
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        user={user}
        onDeleted={() => {
          setDeleteAccountOpen(false);
          setView("home");
          reset();
          notify("Your account has been permanently deleted.", "info");
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={closeSnack} sx={{ borderRadius: 4 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
